/**
 * The fire on the title screen, drawn over the whole window so its light is the page's light.
 * The flame is a cone of luminous sheets, ray-marched in a WebGL2 fragment shader: each sample
 * is pushed through a few octaves of rising turbulence, twisted about the axis, and glows by its
 * closeness to the cone's surface, in an orange that the tone map turns yellow and white where the light piles up.
 * Under it a bed of coals and a pool of light on the ground; around it warm air; above it sparks
 * that rise past the name. It burns gently: slow tongues, a soft flicker, a sway in the draft of a
 * passing pointer. When the page opens it catches: embers lift off the glowing coals out of the
 * dark, and the whole flame fades in behind them. It reports its brightness so the page can be lit
 * by it. Nothing is loaded: the whole fire is this code.
 */

const VERT = `#version 300 es
in vec2 p;
void main() { gl_Position = vec4(p, 0.0, 1.0); }`;

const FRAG = `#version 300 es
precision highp float;
uniform vec2 res;
uniform float time;
uniform float heat;
uniform float flick;
uniform float light;
uniform vec2 base;
uniform float size;
uniform float wind;
uniform float dpr;
uniform int steps;
out vec4 outColor;

float hash(float n) { return fract(sin(n * 12.9898) * 43758.5453); }

float noise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  f *= f * (3.0 - 2.0 * f);
  float n = dot(i, vec2(1.0, 57.0));
  return mix(mix(hash(n), hash(n + 1.0), f.x), mix(hash(n + 57.0), hash(n + 58.0), f.x), f.y);
}

void main() {
  vec2 uv = (gl_FragCoord.xy - 0.5 * res) / res.y;
  // Fire space: the coals at 0, the tips near y = 1, one unit = the hearth's height.
  vec2 s = (uv - base) / size;
  float t = time;
  // Everything the fire gives off scales with its light, which rises out of the dark on load.
  float k = heat * flick * light;
  // Interleaved gradient noise: dithers the march and the dark gradients.
  float ign = fract(52.9829189 * fract(dot(gl_FragCoord.xy, vec2(0.06711056, 0.00583715))));
  vec3 col = vec3(0.0);

  // One unit of fire space in device pixels: sparks are sized in pixels, so a spark stays a spark.
  float px = size * res.y;

  // The flame, marched only where it can be.
  // The flame's own frame: it leans with the draft, more the higher it reaches.
  vec2 f = vec2(s.x - wind * 0.46 * s.y * s.y, s.y);
  if (abs(f.x) < 0.7 && f.y > -0.15 && f.y < 1.4) {
    vec3 rd = normalize(vec3(s * 0.58, -1.0));
    vec3 glow = vec3(0.0);
    float z = 3.4 + 0.08 * ign;
    for (int i = 0; i < 64; i++) {
      if (i >= steps) break;
      vec3 p = z * rd;
      p.z += 5.0;
      float y = p.y;
      vec3 q = p;
      // The column twists as it climbs and widens a little, as rising air would carry it.
      float a = y * 0.5 - t * 0.45;
      q.xz = mat2(cos(a), -sin(a), sin(a), cos(a)) * q.xz / (1.0 + 0.12 * max(y, 0.0));
      // Rising turbulence: each octave scrolls upward and folds the sheets into tongues. It moves
      // them sideways far more than up or down, so they stand like flames and never lie flat, and
      // eases off as they climb, so the flame gathers to points.
      vec3 free = vec3(1.0, 0.3, 1.0) * (0.75 - 0.4 * smoothstep(0.2, 2.6, y));
      // Each octave is turned a little about the axis and rises at its own pace, so their waves
      // never line up into a pattern.
      const mat2 turn = mat2(0.6, -0.8, 0.8, 0.6);
      for (float o = 4.5; o < 40.0; o /= 0.6) {
        q += cos((q.yzx - vec3(t * 1.6 * sqrt(o * 0.4), t * 0.6, o)) * o) / o * free;
        q.xz = turn * q.xz;
      }
      // The draft bends the flame as a whole, more the higher it reaches.
      q.x -= wind * y * y * 0.16;
      // Distance to a cone that narrows to the tips; light gathers at its surface.
      float d = 0.006 + abs(length(q.xz) + q.y * 0.14 - 0.42) / 6.0;
      // Thinning out up the flame and gone before the cone's point (y = 3), past which its axis
      // would glow as a hairline.
      float body = smoothstep(-0.1, 0.25, y) * (1.0 - smoothstep(0.9, 2.8, y));
      // One deep orange, redder toward the tips; the tone map turns the brightest sheets yellow and white.
      glow += mix(vec3(1.0, 0.42, 0.12), vec3(1.0, 0.2, 0.05), smoothstep(0.3, 2.4, y)) * body / d;
      z += d;
      if (z > 6.6) break;
    }
    // Soft at the edges of the marched box, so no box shows.
    float edge = smoothstep(0.7, 0.45, abs(f.x)) * smoothstep(-0.15, 0.0, f.y) * smoothstep(1.4, 1.1, f.y);
    col += glow * 0.0005 * k * edge;
  }

  // The heart of the fire: a white-yellow core low in the flame, where it burns hottest.
  vec2 h = (f - vec2(0.0, 0.14)) * vec2(7.0, 3.2);
  col += vec3(1.0, 0.62, 0.26) * exp(-dot(h, h)) * 0.55 * k * smoothstep(-0.04, 0.06, f.y);

  // A bed of coals: a flattened ellipse of embers that pulse unevenly. They glow ahead of the
  // flame as the fire lights, so the first embers rise off something.
  float bed = smoothstep(0.42, 0.0, length(s * vec2(1.0, 4.5) - vec2(0.0, 0.1)));
  float ember = pow(noise(s * vec2(22.0, 30.0) + vec2(0.0, t * 0.3)) * noise(s * vec2(9.0, 14.0) - t * 0.18), 1.5);
  col += vec3(1.0, 0.25, 0.04) * bed * (0.15 + 2.2 * ember) * 0.6 * heat * flick * sqrt(light);

  // Light thrown on the ground, wide and low; warm air around the flame, faint and high.
  vec2 g = s * vec2(0.85, 5.0);
  col += vec3(0.55, 0.12, 0.02) * exp(-dot(g, g) * 1.4) * 0.34 * k;
  vec2 air = (f - vec2(0.0, 0.5)) * vec2(1.2, 0.8);
  col += vec3(0.42, 0.1, 0.02) * exp(-dot(air, air) * 2.2) * 0.07 * k;

  // Sparks: a few, buoyant, quick off the coals and slowing as they cool; some drift past the
  // name. They come last, once the fire is up.
  float sparks = smoothstep(0.6, 1.0, light) * k;
  for (int i = 0; i < 14; i++) {
    float fi = float(i);
    float r1 = hash(fi + 1.3), r2 = hash(fi + 7.1), r3 = hash(fi + 3.7);
    float life = fract(t * (0.06 + 0.05 * r1) + r2);
    float up = 1.0 - (1.0 - life) * (1.0 - life);
    vec2 sp = vec2((r3 - 0.5) * 0.3, 0.15 + up * (1.2 + 1.1 * r2));
    sp.x += (sin(t * 0.8 + fi * 2.3) * 0.06 + (r2 - 0.5) * 0.7) * life + wind * 0.35 * life;
    // Sized in device pixels so a spark stays a spark at any hearth size.
    vec2 d = (s - sp) * px;
    float r = (0.7 + 1.1 * r1) * dpr;
    float fade = (1.0 - life) * smoothstep(0.0, 0.08, life) * (0.7 + 0.3 * sin(t * (4.0 + 5.0 * r3) + fi));
    col += vec3(1.0, 0.58, 0.22) * (exp(-dot(d, d) / (r * r)) * 1.6 + exp(-dot(d, d) / (r * r * 30.0)) * 0.06) * fade * sparks;
  }

  // Catching: as the fire lights, a puff of embers lifts off the coals out of the dark, each at its
  // own moment, ahead of the flame, which fades in behind them. They are gone once it is up.
  if (t < 4.5) {
    for (int i = 0; i < 22; i++) {
      float fi = float(i);
      float r1 = hash(fi + 11.3), r2 = hash(fi + 17.9), r3 = hash(fi + 23.1);
      float age = (t - 0.25 - 1.5 * r1) / (1.4 + 0.9 * r2);
      if (age <= 0.0 || age >= 1.0) continue;
      float up = 1.0 - (1.0 - age) * (1.0 - age);
      vec2 sp = vec2((r3 - 0.5) * 0.34, 0.03 + up * (0.45 + 0.65 * r2));
      sp.x += sin(age * 5.0 + fi) * 0.025 + (r3 - 0.5) * 0.3 * up;
      vec2 d = (s - sp) * px;
      float r = (0.8 + 1.0 * r2) * dpr;
      float fade = smoothstep(0.0, 0.15, age) * (1.0 - age) * (1.0 - age);
      col += vec3(1.0, 0.55, 0.2) * (exp(-dot(d, d) / (r * r)) * 1.8 + exp(-dot(d, d) / (r * r * 30.0)) * 0.08) * fade * heat;
    }
  }

  // Filmic tone map; black stays black. A whisper of dither keeps the dark gradients smooth.
  col = 1.0 - exp(-col * 1.4);
  col = pow(col, vec3(0.9)) + (ign - 0.5) / 255.0;
  outColor = vec4(col, 1.0);
}`;

export interface Fire {
  /** Flare (`> 1`) or settle (`1`); eased. */
  setHeat(h: number): void;
  stop(): void;
}

/** The fire comes up out of the dark: a beat of black (the first embers rise in it), then this many
 *  seconds of rising light. */
const DARK_S = 0.5;
const RISE_S = 3.2;
/** The draft's spring: a slow sway that leans a little past upright and settles (period ~1.4 s). */
const WIND_W = (2 * Math.PI) / 1.4;
const WIND_DAMP = 0.6;

/** The fire's flicker at time `t` (seconds): a few slow, unrelated beats, about 0.94–1.06. */
function flickerAt(t: number): number {
  return 1 + 0.025 * Math.sin(t * 3.1) + 0.018 * Math.sin(t * 5.3 + 1.3) + 0.015 * Math.sin(t * 1.2 + 0.4) + 0.006 * Math.sin(t * 11.7 + 2.9);
}

/** Smootherstep: starts and ends at rest, so light neither pops on nor stops short. */
const ease = (x: number) => (x <= 0 ? 0 : x >= 1 ? 1 : x * x * x * (x * (6 * x - 15) + 10));

/**
 * Starts the fire on `canvas` (sized to the window), burning in the box of `hearth`: the coals
 * at its bottom edge, the tips near its top. `still` renders a single frame (reduced motion).
 * `onLight` receives the fire's brightness each frame (rising from 0, then about 0.94–1.2).
 */
export function startFire(canvas: HTMLCanvasElement, hearth: HTMLElement, still = false, onLight?: (b: number) => void): Fire | null {
  const gl = canvas.getContext('webgl2', { antialias: false, alpha: false, powerPreference: 'high-performance' });
  if (!gl) return null;
  const compile = (type: number, src: string) => {
    const s = gl.createShader(type)!;
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s) ?? 'shader');
    return s;
  };
  const prog = gl.createProgram()!;
  try {
    gl.attachShader(prog, compile(gl.VERTEX_SHADER, VERT));
    gl.attachShader(prog, compile(gl.FRAGMENT_SHADER, FRAG));
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(prog) ?? 'link');
  } catch (e) {
    console.warn('[fire]', (e as Error).message);
    return null;
  }
  gl.useProgram(prog);
  gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
  const loc = gl.getAttribLocation(prog, 'p');
  gl.enableVertexAttribArray(loc);
  gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
  const u = Object.fromEntries(['res', 'time', 'heat', 'flick', 'light', 'base', 'size', 'wind', 'dpr', 'steps'].map((n) => [n, gl.getUniformLocation(prog, n)]));

  // Where the hearth sits, in CSS pixels; measured on layout changes, not every frame.
  let box = hearth.getBoundingClientRect();
  const measure = () => (box = hearth.getBoundingClientRect());
  const ro = new ResizeObserver(measure);
  ro.observe(document.documentElement);
  ro.observe(hearth);
  void document.fonts?.ready.then(measure);

  // The draft: a passing pointer pushes the flame, more the nearer and faster it passes.
  let wind = 0, windV = 0, lastX = NaN;
  const onMove = (e: PointerEvent) => {
    const dx = Number.isNaN(lastX) ? 0 : e.clientX - lastX;
    lastX = e.clientX;
    const cx = box.left + box.width / 2, cy = box.top + box.height * 0.55;
    const r = box.height * 0.8;
    const near = Math.exp(-((e.clientX - cx) ** 2 + (e.clientY - cy) ** 2) / (r * r));
    windV += (dx / box.height) * near * 5;
  };
  if (!still) addEventListener('pointermove', onMove, { passive: true });

  let heat = 1, target = 1, raf = 0, last = performance.now();
  const t0 = last;
  // Quality adapts to the machine: render scale and march steps step down when frames run long.
  const maxScale = Math.min(devicePixelRatio || 1, 1.5);
  let scale = maxScale, steps = 56, slow = 0, fast = 0;
  const frame = (now: number) => {
    // The page moved on (the game replaced it): stop drawing and give the context back.
    if (!canvas.isConnected) return release();
    const ms = now - last;
    const dt = Math.min(0.05, ms / 1000);
    last = now;
    if (!still) {
      if (ms > 24) slow++;
      else if (ms < 14) fast++;
      if (slow > 20) {
        slow = fast = 0;
        if (steps > 36) steps -= 8;
        else scale = Math.max(0.5, scale * 0.8);
      } else if (fast > 240) {
        slow = fast = 0;
        if (scale < maxScale) scale = Math.min(maxScale, scale * 1.15);
        else if (steps < 56) steps += 8;
      }
    }
    heat += (target - heat) * (1 - Math.exp(-dt * 2.5));
    // Semi-implicit spring for the draft, clamped so a wild fling can't fold the flame over.
    windV += (-WIND_W * WIND_W * wind - 2 * WIND_DAMP * WIND_W * windV) * dt;
    wind = Math.max(-1.2, Math.min(1.2, wind + windV * dt));
    const t = still ? 7.3 : (now - t0) / 1000;
    const flick = still ? 1 : flickerAt(t);
    const light = still ? 1 : ease((t - DARK_S) / RISE_S);
    const w = Math.round(innerWidth * scale), h = Math.round(innerHeight * scale);
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
    }
    // The hearth's bottom centre and height, in the shader's units (fractions of the window height, centred).
    gl.viewport(0, 0, w, h);
    gl.uniform2f(u.res, w, h);
    gl.uniform1f(u.time, t);
    gl.uniform1f(u.heat, heat);
    gl.uniform1f(u.flick, flick);
    gl.uniform1f(u.light, light);
    gl.uniform2f(u.base, (box.left + box.width / 2 - innerWidth / 2) / innerHeight, (innerHeight / 2 - box.bottom) / innerHeight);
    gl.uniform1f(u.size, box.height / innerHeight);
    gl.uniform1f(u.wind, wind);
    gl.uniform1f(u.dpr, scale);
    gl.uniform1i(u.steps, steps);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    onLight?.(heat * flick * light);
    if (!still) raf = requestAnimationFrame(frame);
  };
  const release = () => {
    cancelAnimationFrame(raf);
    ro.disconnect();
    removeEventListener('pointermove', onMove);
    gl.getExtension('WEBGL_lose_context')?.loseContext();
  };
  raf = requestAnimationFrame(frame);
  return {
    setHeat(h) {
      target = h;
      if (still) {
        heat = h;
        raf = requestAnimationFrame(frame);
      }
    },
    stop: release,
  };
}
